/**
 * The consequence echo — what saving this number will DO (VRG-459).
 *
 * ## Why this exists at all
 *
 * Type and range checks are table stakes and they cannot catch the failure that
 * matters: a units error is a valid number. The defence the epic settled on is to
 * show the CONSEQUENCE before the save, because a wrong magnitude becomes an absurd
 * OUTPUT — "moves Return on Vera from 2.9x to 41x" — and a person catches that
 * instantly where a validator never will.
 *
 * ## ⚠️ IT ONLY EVER STATES A FIGURE IT CAN DERIVE EXACTLY
 *
 * There are two cases and they are NOT the same claim:
 *
 *   RESCALE — the metric is already contributing, so the response carries its
 *             current cents and we know the input those cents were computed from.
 *             Every input-driven formula in `ROI_CONTRIBUTION_RESOLVERS` is LINEAR
 *             in its input (return-fraud is `annualLoss x rangeDays/365 x coverage`;
 *             the other two have no resolver at all), so the new figure is the old
 *             one times the ratio of the inputs — exact, no assumption.
 *
 *   UNKNOWN — the metric is withheld, so there is no current figure to scale from.
 *             The client CANNOT compute one: the formula needs the company's
 *             certificate-coverage rate and the range in days, and the ROI response
 *             exposes neither. So this returns NO projected figure. Guessing one —
 *             assuming full coverage, or borrowing the page's date range — would put
 *             a fabricated number in front of the reader as a check against
 *             fabricated numbers, which is worse than saying nothing.
 *
 * The magnitude defence still fires in the UNKNOWN case: the parsed value and its
 * words render regardless (`metricInputFormat`), which is what actually catches the
 * 100x slip. What is lost is only the "and the multiple becomes N" half, and the
 * honest fix for that is a server-side preview endpoint — noted, not faked.
 */
import type {
  IMetricInputDefinition,
  IMetricInputValue,
  IRoiSummary,
} from '@repo/types'

import { formatCents, formatStoredValue } from './metricInputFormat'

export type MetricInputConsequence = {
  /**
   * `exact` — every affected metric could be rescaled, so `lines` states real
   * resulting figures. `partial` — some could, some could not. `magnitude-only` —
   * none could, so nothing but the parsed value is being asserted.
   */
  confidence: 'exact' | 'partial' | 'magnitude-only'
  /** One sentence per affected metric whose new figure is derivable. */
  lines: string[]
  /** The multiple sentence, when the numerator moved by a derivable amount. */
  multipleLine: string | null
  /** Metrics this input feeds that cannot yet render a figure at all. */
  unresolvedTitles: string[]
}

const formatMultiple = (m: number): string =>
  // Two decimals below 10 (2.94x reads as a real measurement), one above (41.2x) —
  // past ten the second decimal is precision the underlying figures do not have.
  m < 10 ? `${m.toFixed(2)}x` : `${m.toFixed(1)}x`

/**
 * What saving `nextStored` for `definition` would do, given the ROI response the
 * reader is currently looking at.
 *
 * `roi` may be null (the Settings page can load before, or without, an ROI
 * response). That degrades to `magnitude-only`, which is correct: with no current
 * figures there is nothing to state a change against.
 */
export const describeConsequence = ({
  definition,
  nextStored,
  current,
  roi,
  entryCurrency,
  reportingCurrency,
}: {
  definition: IMetricInputDefinition
  /** The value about to be saved, in the API's units. */
  nextStored: number
  /** What is stored today for this key, or null when nothing is. */
  current: IMetricInputValue | null
  roi: IRoiSummary | null
  /**
   * VRG-497 — the currency the FIELD is asking in (a stored value's own, or the
   * company's for a new entry). Only reaches `describeSettingLine`; see below for why
   * the ROI lines use a different one.
   */
  entryCurrency?: string
  /**
   * The currency the ROI FIGURES are denominated in. Distinct from `entryCurrency`, and
   * conflating them is the bug this parameter exists to make impossible: a brand
   * editing a stale USD figure would otherwise be told "Return fraud prevented goes
   * from $12,000 to $14,000" about a metric whose amounts are GBP.
   *
   * In practice the two only differ while a figure IS stale — and a stale figure's
   * metric is withheld server-side (`input_currency`), so there is no contribution to
   * rescale and the lines below are not produced at all. The parameter is threaded
   * anyway rather than assumed equal: an assumption that happens to hold is how the
   * next currency bug gets in.
   */
  reportingCurrency?: string
}): MetricInputConsequence => {
  const lines: string[] = []
  const unresolvedTitles: string[] = []

  // Metric ids and titles arrive paired and same-ordered from the catalogue, which
  // is what lets a sentence name the metric rather than its id.
  const affected = definition.metric_ids.map((id, i) => ({
    id,
    title: definition.metric_titles[i] ?? id,
  }))

  const contributionFor = (id: string) =>
    roi?.contributions.find(c => c.metric_id === id) ?? null

  /**
   * The rescale is only valid when the CURRENT figure was computed from a CURRENT
   * input, and that input is not zero.
   *
   * The zero guard is not defensive padding — `oldStored === 0` makes the ratio
   * infinite (or NaN), and a stored zero is legal for every spec here (`min: 0`).
   * Without it, changing a figure from 0% would render "Infinityx".
   */
  const oldStored = current?.value ?? null
  const canRescale =
    typeof oldStored === 'number' && oldStored !== 0 && Number.isFinite(nextStored)

  let deliveredDeltaCents = 0
  let rescaled = 0

  for (const metric of affected) {
    const contribution = contributionFor(metric.id)

    if (!contribution || !canRescale) {
      // Either the metric is withheld (no figure to scale) or nothing is stored yet
      // (no ratio). Both land here, and neither may assert a resulting number.
      unresolvedTitles.push(metric.title)
      continue
    }

    const ratio = nextStored / (oldStored as number)
    const nextCents = Math.round(contribution.cents * ratio)
    deliveredDeltaCents += nextCents - contribution.cents
    rescaled += 1

    lines.push(
      `${metric.title} goes from ${formatCents(contribution.cents, reportingCurrency)} to ${formatCents(nextCents, reportingCurrency)}.`,
    )
  }

  /**
   * The multiple, recomputed over the SAME denominator.
   *
   * Vera spend does not depend on a brand input, so the denominator is unchanged by
   * construction and only the numerator moves. `total_cents` of zero yields no
   * multiple at all, matching `IRoiSummary.multiple`'s own contract — a brand on a
   * free plan has no denominator, and dividing by zero to show an infinite return
   * would be the single most embarrassing number on the page.
   */
  let multipleLine: string | null = null
  const spendCents = roi?.spend?.total_cents ?? 0
  if (roi && deliveredDeltaCents !== 0 && spendCents > 0) {
    const nextDelivered = roi.value_delivered_cents + deliveredDeltaCents
    const before = roi.value_delivered_cents / spendCents
    const after = nextDelivered / spendCents
    multipleLine = `Return on Vera moves from ${formatMultiple(before)} to ${formatMultiple(after)}.`
  }

  const confidence: MetricInputConsequence['confidence'] =
    rescaled === 0
      ? 'magnitude-only'
      : unresolvedTitles.length === 0
        ? 'exact'
        : 'partial'

  return { confidence, lines, multipleLine, unresolvedTitles }
}

/**
 * The headline of the echo — always present, always exact.
 *
 * "Sets Annual return-fraud loss to $250,000.00." This is the half that does not
 * depend on the ROI response at all, and it is the half that catches a units error.
 */
export const describeSettingLine = (
  definition: IMetricInputDefinition,
  nextStored: number,
  /** VRG-497 — the currency the field is asking in, so the echo matches the box. */
  entryCurrency?: string,
): string =>
  `Sets ${definition.label} to ${formatStoredValue(definition, nextStored, entryCurrency)}.`
